import { ReactElement, useEffect, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { Formik } from 'formik'

import { getUnprocessableErrors, isPasswordConflictError } from 'utils/error'
import { VisibilityIcon } from '@10xLabs/react-web/components/Icons/Visibility'
import { VisibilityOffIcon } from '@10xLabs/react-web/components/Icons/VisibilityOff'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { schema } from './schema'
import styled from 'styled-components'
import { ApolloError } from '@apollo/client'
import { IResetPasswordFormValues } from 'features/Login/types'

import { Radio } from 'components/v2/Radio'
import { generatePassword } from 'utils/string'

const SecurityReasonsMessage = styled.p`
  font-size: 16px;
`
const Form = styled.form`
  max-width: 350px;
  width: 100%;
  margin: auto;
`

const InputWrapper = styled.div`
  margin-top: 10px;
  position: relative;

  &:first-child {
    margin-top: 0;
  }
`

const ButtonWrapper = styled.div`
  margin-top: 20px;
`

const ShowPasswordWrapper = styled.div`
  position: absolute;
  top: 15px;
  right: 10px;
  font-size: 13px;
  color: #1976d2;
  cursor: pointer;
`

const ErrorMessageWrapper = styled.div`
  margin-bottom: 5px;
`
const RadioLabel = styled.div`
  font-size: 12px;
  text-transform: none;
`
const initialValues = {
  confirmPassword: '',
  password: ''
}

const messages = defineMessages({
  pwdPlaceholder: {
    id: 'login.reset.pwdPlaceholder',
    defaultMessage: 'Password'
  },
  confirmPasswordPlaceholder: {
    id: 'login.reset.confirmPasswordPlaceholder',
    defaultMessage: 'Confirm password'
  }
})

interface ILoginFormProps {
  error?: ApolloError
  loading: boolean
  handleSubmit: (values: IResetPasswordFormValues) => void
}
const ResetPasswordForm = ({ error, loading, handleSubmit }: ILoginFormProps): ReactElement => {
  const intl = useIntl()
  const [isShowingPassword, showPassword] = useState(false)
  const [hasUserSelectGeneratedPassword, setHasUserSelectGeneratedPassword] = useState(false)

  const setFieldValueRef = useRef<TSFixMe>()
  const resetFormRef = useRef<TSFixMe>()
  const formValuesRef = useRef<IResetPasswordFormValues>()

  useEffect(() => {
    resetFormRef.current()
    if (hasUserSelectGeneratedPassword) {
      const generatedPwd = generatePassword()
      setFieldValueRef.current('password', generatedPwd)
      setFieldValueRef.current('confirmPassword', generatedPwd)
    } else {
      setFieldValueRef.current('password', '')
      setFieldValueRef.current('confirmPassword', '')
    }
  }, [hasUserSelectGeneratedPassword])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema(intl)}
      onSubmit={(values): void => {
        if (hasUserSelectGeneratedPassword) {
          navigator.clipboard.writeText(values.password as string)
        }
        handleSubmit({ password: values.password, confirmPassword: values.confirmPassword })
      }}
    >
      {(props): ReactElement => {
        const { values, touched, errors, handleChange, handleBlur, handleSubmit } = props
        setFieldValueRef.current = props.setFieldValue
        formValuesRef.current = values
        resetFormRef.current = props.resetForm

        return (
          <Form onSubmit={handleSubmit}>
            <SecurityReasonsMessage>
              <FormattedMessage
                id="login.reset.securityReasons"
                defaultMessage="For security reasons please generate a new password"
              />
            </SecurityReasonsMessage>

            <Radio
              id="selectPassword"
              name="selectPassword"
              checked={!hasUserSelectGeneratedPassword}
              onChange={(): void => setHasUserSelectGeneratedPassword(!hasUserSelectGeneratedPassword)}
            >
              <RadioLabel>
                <FormattedMessage id="login.reset.selectPassword" defaultMessage="Create password" />
              </RadioLabel>
            </Radio>
            <Radio
              id="generatePassword"
              name="generatePassword"
              checked={hasUserSelectGeneratedPassword}
              onChange={(): void => setHasUserSelectGeneratedPassword(!hasUserSelectGeneratedPassword)}
            >
              <RadioLabel>
                <FormattedMessage id="login.reset.generatePassword" defaultMessage="Automatically generate password" />
              </RadioLabel>
            </Radio>
            {hasUserSelectGeneratedPassword ? (
              <>
                <InputWrapper>
                  <Input
                    id="password"
                    type={isShowingPassword ? 'text' : 'password'}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={intl.formatMessage(messages.pwdPlaceholder)}
                    touched={touched.password}
                    errors={errors.password}
                    readOnly
                  />

                  <ShowPasswordWrapper onClick={(): void => showPassword(!isShowingPassword)}>
                    {isShowingPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </ShowPasswordWrapper>
                </InputWrapper>
              </>
            ) : (
              <>
                <InputWrapper>
                  <Input
                    id="password"
                    type={isShowingPassword ? 'text' : 'password'}
                    value={props.values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={intl.formatMessage(messages.pwdPlaceholder)}
                    touched={touched.password}
                    errors={errors.password}
                  />

                  <ShowPasswordWrapper onClick={(): void => showPassword(!isShowingPassword)}>
                    {isShowingPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </ShowPasswordWrapper>
                </InputWrapper>
                <InputWrapper>
                  <Input
                    id="confirmPassword"
                    type={isShowingPassword ? 'text' : 'password'}
                    value={props.values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={intl.formatMessage(messages.confirmPasswordPlaceholder)}
                    touched={touched.confirmPassword}
                    errors={errors.confirmPassword}
                  />

                  <ShowPasswordWrapper onClick={(): void => showPassword(!isShowingPassword)}>
                    {isShowingPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </ShowPasswordWrapper>
                </InputWrapper>
              </>
            )}
            <ButtonWrapper>
              {error ? (
                <ErrorMessageWrapper>
                  {isPasswordConflictError(error) ? (
                    <ErrorMessage>
                      <FormattedMessage
                        id="login.reset.passwordConflict"
                        defaultMessage="Your new password matches a previous password. Please choose a different one"
                      />
                    </ErrorMessage>
                  ) : (
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage id="login.reset.error" defaultMessage="An error occurred" />
                    </ErrorMessage>
                  )}
                </ErrorMessageWrapper>
              ) : null}

              {hasUserSelectGeneratedPassword ? (
                <Button type="submit" kind="primary" disabled={loading} loading={loading} fullWidth>
                  <FormattedMessage id="login.reset.copyAndSubmit" defaultMessage="Copy pasword and continue" />
                </Button>
              ) : (
                <Button type="submit" kind="primary" disabled={loading} loading={loading} fullWidth>
                  <FormattedMessage id="login.reset.submit" defaultMessage="Update password" />
                </Button>
              )}
            </ButtonWrapper>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ResetPasswordForm
